import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ViewPatientModal from '../components/ViewPatientModal';
import EditPatientModal from '../components/EditPatientModal';
import { BILL_TYPES } from '../constants/billingTypes';
import { compareByDutyThenName } from '../constants/shifts';
import DutyTypePill from '../components/DutyTypePill';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface TreatingDetail {
  doctor: string;
  department: string;
  hospital: string;
}

interface Patient {
  id: number;
  patient_name: string;
  status: string;
  age: string | number;
  sex: string;
  case_start_date: string;
  duty_type?: string | null;
  case_manager?: string | null;
  address: string;
  treating_details?: TreatingDetail[];
  patient_clients: { client_name?: string; name?: string; contact_number_1?: string; contact_number?: string }[];
}


// The first client on the case — the name the Client Name column shows, and so
// the one the alphabetical sort has to read by.
const clientNameOf = (p: Patient) => {
  const c = [...(p.patient_clients || [])].sort((a: any, b: any) => (a.id || 0) - (b.id || 0))[0];
  return (c?.client_name || c?.name || '').trim() || p.patient_name;
};

const formatDate = (d: string | undefined | null) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
};

const BillingIndex: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);

  const [viewPatientId, setViewPatientId] = useState<number | null>(null);
  const [editPatientId, setEditPatientId] = useState<number | null>(null);
  const [pickerPatient, setPickerPatient] = useState<Patient | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/patients/?limit=200`, { headers });
      setPatients(res.data);
    } catch (err) {
      console.error('Failed to fetch patients', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPatients(); }, []);

  const filtered = patients.filter((p) => {
    const term = searchTerm.toLowerCase();
    const anyClientMatch = (p.patient_clients || []).some(c => {
      const name = (c.client_name || c.name || '').toLowerCase();
      return name.includes(term);
    });
    const td0 = p.treating_details?.[0];
    return (
      p.patient_name.toLowerCase().includes(term) ||
      anyClientMatch ||
      `LHHS${p.id}`.toLowerCase().includes(term) ||
      (td0?.doctor || '').toLowerCase().includes(term) ||
      (td0?.hospital || '').toLowerCase().includes(term) ||
      (p.address || '').toLowerCase().includes(term) ||
      (p.duty_type || '').toLowerCase().includes(term) ||
      (p.case_manager || '').toLowerCase().includes(term)
    );
  // Duty type in the client's reading order (08 Hrs, 12 Dual, 12 Day, 12 Night,
  // 24 Hrs), then A→Z on client name. Replaces the old case-start ordering.
  }).sort((a, b) => compareByDutyThenName(
    { duty_type: a.duty_type, name: clientNameOf(a) },
    { duty_type: b.duty_type, name: clientNameOf(b) },
  ));

  // ── Styles matching PatientDashboard exactly ─────────────────────────────
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '1.25rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
    title: { margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.85rem' },
    searchInput: {
      padding: '0.5rem 0.75rem 0.5rem 2rem', borderRadius: '8px', border: '1px solid #cbd5e1',
      fontSize: '0.85rem', color: '#334155', width: '240px', outline: 'none', backgroundColor: '#fff',
    },
    tableCard: {
      backgroundColor: 'white', borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.025)',
      border: '1px solid #e2e8f0', overflow: 'hidden',
    },
    tableWrapper: { width: '100%', overflowX: 'auto' },
    table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', color: '#1d2631' },
    thead: { backgroundColor: '#86afd8', borderBottom: '1px solid #e2e8f0' },
    th: {
      padding: '0.55rem 0.3rem', color: '#475569', fontWeight: 700, fontSize: '0.68rem',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      border: '1px solid #c5cbd3', textAlign: 'center', lineHeight: 1.3,
    },
    tbody: { backgroundColor: 'white' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.1s' },
    td: { padding: '0.45rem 0.35rem', verticalAlign: 'middle', border: '1px solid #c5cbd3', textAlign: 'center', fontSize: '0.76rem', wordBreak: 'break-word', lineHeight: 1.35 },
    textBtn: {
      padding: '0.35rem 0.7rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
      cursor: 'pointer', border: 'none', transition: 'all 0.2s', display: 'inline-block',
    },
    // View and Edit stack inside their shared cell, centred rather than
    // stretched so they stay compact instead of filling the column.
    // Stretch, not centre + min-width: at 4% the column can be narrower than a
    // fixed button on a smaller screen, and a stretched button simply takes the
    // width it's given instead of spilling out of the cell.
    btnStack: { display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'stretch' },
    textBtnSm: { padding: '0.15rem 0.3rem', fontSize: '0.65rem' },
  };

  const btnView   = { background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)' };
  const btnEdit   = { background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' };
  const btnBill   = { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(22,163,74,0.2)' };
  const btnCreate = { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', boxShadow: '0 4px 6px -1px rgba(217,119,6,0.2)' };

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Billing Module</h1>
          <p style={styles.subtitle}>View and manage invoices for all patients.</p>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg width="16" height="16" fill="none" stroke="#94a3b8" viewBox="0 0 24 24"
            style={{ position: 'absolute', left: '10px', pointerEvents: 'none' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search name, doctor, hospital..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </header>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>Loading patient data...</div>
      ) : (
        <div style={styles.tableCard}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  {/* Percentages summing to 100, so the table fits its container
                      and never scrolls sideways. Room for Duty Type and Case
                      Manager comes from Address / Doctor / Hospital / Patient —
                      the four roomiest text columns — and from merging View and
                      Edit into one narrow cell. */}
                  <th style={{ ...styles.th, width: '3%' }}>Sl.<br/>No</th>
                  <th style={{ ...styles.th, width: '5%' }}>Case<br/>Start</th>
                  <th style={{ ...styles.th, width: '5%' }}>Duty<br/>Type</th>
                  <th style={{ ...styles.th, width: '9%' }}>Client Name</th>
                  <th style={{ ...styles.th, width: '7%' }}>Mobile</th>
                  <th style={{ ...styles.th, width: '9%' }}>Patient Name</th>
                  <th style={{ ...styles.th, width: '3%' }}>Age /<br/>Sex</th>
                  <th style={{ ...styles.th, width: '12%' }}>Address</th>
                  <th style={{ ...styles.th, width: '13%' }}>Doctor<br/>Name</th>
                  <th style={{ ...styles.th, width: '10%' }}>Hospital<br/>Name</th>
                  <th style={{ ...styles.th, width: '8%' }}>Case<br/>Manager</th>
                  {/* View and Edit share a cell, stacked — two columns of one
                      small button each cost more width than the pair needs. */}
                  <th style={{ ...styles.th, width: '4%' }}>View / Edit<br/>Client</th>
                  <th style={{ ...styles.th, width: '6%' }}>View<br/>Bill</th>
                  <th style={{ ...styles.th, width: '6%' }}>Create<br/>Bill</th>
                </tr>
              </thead>
              <tbody style={styles.tbody}>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={14} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                      No patients found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((p, idx) => {
                    const clients = [...(p.patient_clients || [])].sort((a: any, b: any) => (a.id || 0) - (b.id || 0));
                    const td0 = p.treating_details?.[0];
                    const doctor = td0?.doctor || '—';
                    const hospital = td0?.hospital || '—';

                    return (
                      <tr
                        key={p.id}
                        style={{ ...styles.tr, backgroundColor: hoverRowId === p.id ? '#f8fafc' : 'white' }}
                        onMouseEnter={() => setHoverRowId(p.id)}
                        onMouseLeave={() => setHoverRowId(null)}
                      >
                        {/* Sl. No */}
                        <td style={{ ...styles.td, backgroundColor: '#f8fafc', fontWeight: 600, color: '#94a3b8' }}>
                          {idx + 1}
                        </td>

                        {/* Case Start Date */}
                        <td style={styles.td}>
                          {formatDate(p.case_start_date)}
                        </td>

                        {/* Duty Type — up front, since the list is ordered by it */}
                        <td style={styles.td}><DutyTypePill dutyType={p.duty_type} /></td>

                        {/* Client Name */}
                        <td style={{ ...styles.td, textAlign: 'left' }}>
                          {clients.length === 0 ? '—' : clients.map((c, i) => (
                            <div key={i} style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem', marginBottom: i < clients.length - 1 ? '2px' : 0 }}>
                              {c.client_name || c.name || '—'}
                            </div>
                          ))}
                        </td>

                        {/* Mobile */}
                        <td style={styles.td}>
                          {clients.length === 0 ? '—' : clients.map((c, i) => (
                            <div key={i} style={{ fontSize: '0.76rem', marginBottom: i < clients.length - 1 ? '2px' : 0 }}>
                              {c.contact_number_1 || c.contact_number || '—'}
                            </div>
                          ))}
                        </td>

                        {/* Patient Name + CID */}
                        <td style={{ ...styles.td, textAlign: 'left' }}>
                          <div style={{ color: '#0f172a', fontSize: '0.78rem' }}>{p.patient_name}</div>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace', marginTop: '1px' }}>
                            LHHS{p.id}
                          </div>
                        </td>

                        {/* Age / Sex */}
                        <td style={styles.td}>
                          {p.age || '—'}/{p.sex ? p.sex.charAt(0).toUpperCase() : '—'}
                        </td>

                        

                        {/* Address */}
                        <td style={{ ...styles.td, textAlign: 'left', color: '#1e293b' }}>
                          {p.address || '—'}
                        </td>

                        {/* Doctor Name */}
                        <td style={styles.td}>{doctor}</td>

                        {/* Hospital Name */}
                        <td style={styles.td}>{hospital}</td>

                        {/* Case Manager */}
                        <td style={styles.td}>{p.case_manager || '—'}</td>

                        {/* View + Edit client */}
                        <td style={styles.td}>
                          <div style={styles.btnStack}>
                            <button style={{ ...styles.textBtn, ...styles.textBtnSm, ...btnView }} onClick={() => setViewPatientId(p.id)}>
                              View
                            </button>
                            <button style={{ ...styles.textBtn, ...styles.textBtnSm, ...btnEdit }} onClick={() => setEditPatientId(p.id)}>
                              Edit
                            </button>
                          </div>
                        </td>

                        {/* View Bill */}
                        <td style={styles.td}>
                          <button style={{ ...styles.textBtn, ...btnBill }} onClick={() => navigate(`/billing/patient/${p.id}`)}>
                            View Bill
                          </button>
                        </td>

                        {/* Create Bill */}
                        <td style={styles.td}>
                          <button style={{ ...styles.textBtn, ...btnCreate }} onClick={() => setPickerPatient(p)}>
                            Create Bill
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bill type picker modal */}
      {pickerPatient && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.6)',
            zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPickerPatient(null)}
        >
          <div
            style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '2rem',
              width: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
              Select Bill Type
            </h3>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#64748b' }}>
              Choose the type of invoice to create for {pickerPatient.patient_name}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {BILL_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    const p = pickerPatient;
                    setPickerPatient(null);
                    if (t.key === 'consumables') {
                      navigate(`/billing/consumables/new/${p.id}`);
                    } else if (t.key === 'consumable_return') {
                      navigate(`/billing/consumables/return/new/${p.id}`);
                    } else if (t.key === 'equipment_rental') {
                      navigate(`/billing/equipment-rental/new/${p.id}`, { state: { billType: 'equipment_rental' } });
                    } else if (t.key === 'equipment_sale') {
                      navigate(`/billing/equipment-sale/new/${p.id}`, { state: { billType: 'equipment_sale' } });
                    } else {
                      navigate(`/billing/new/${p.id}`, { state: { billType: t.key } });
                    }
                  }}
                  style={{
                    padding: '1rem', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: t.bg, border: `1.5px solid ${t.border}`,
                    borderRadius: '10px', transition: 'transform 0.1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: t.color }}>{t.label}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{t.desc}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setPickerPatient(null)}
              style={{
                marginTop: '1.25rem', width: '100%', padding: '0.6rem',
                background: 'none', border: '1px solid #e2e8f0', borderRadius: '8px',
                cursor: 'pointer', color: '#64748b', fontSize: '0.85rem',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* View patient modal */}
      <ViewPatientModal
        isOpen={viewPatientId !== null}
        patientId={viewPatientId}
        onClose={() => setViewPatientId(null)}
      />

      {/* Edit patient modal */}
      <EditPatientModal
        isOpen={editPatientId !== null}
        patientId={editPatientId}
        onClose={() => setEditPatientId(null)}
        onSuccess={() => { fetchPatients(); setEditPatientId(null); }}
      />
    </div>
  );
};

export default BillingIndex;
